import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { OverrideProp, OverrideRule, OverrideViewport } from '@evo-hub/shared';

export interface ContentBlock {
  id: string;
  type: 'hero' | 'services' | 'features' | 'testimonials' | 'about' | 'cta' | 'faq' | 'contact' | 'custom';
  title: string;
  subtitle: string;
  content: string;
  items: { label: string; description: string }[];
  ctaText: string;
  order: number;
  layout?: 'single' | 'two-col' | 'three-col' | 'carousel' | 'stack';
  imagePlaceholders?: number;
  notes?: string;
  /** Página del sitio a la que pertenece el bloque; bloques viejos sin este
   *  campo pertenecen a la primera página. */
  page?: string;
}

export interface ContentPlan {
  blocks: ContentBlock[];
  /** Páginas del sitio en orden; planes viejos: ausente → ['Inicio']. */
  pages?: string[];
  generatedAt: string;
  lastEditedAt: string;
  reportGeneratedAt?: string;
  reportId?: number;
  reportSlug?: string;
}

export interface ProjectRow {
  id: number;
  contactId: number;
  contactDisplayName?: string | null;
  contactBusinessName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  leadId: string | null;
  type: string;
  subtype: string | null;
  status: string;
  name: string;
  slug: string;
  briefData: string | null;
  contentPlan: string | null;
  templateName: string | null;
  templateSlug: string;
  currentVersion: number;
  previewUrl: string | null;
  storagePath: string | null;
  desiredDeadline: string | null;
  language: string;
  createdAt: number;
  updatedAt: number;
}

export interface ProjectsResult {
  items: ProjectRow[];
  total: number;
  pagination: { limit: number; offset: number; hasMore: boolean };
}

export interface ProjectFilters {
  status?: string;
  contactId?: number;
  type?: string;
  subtype?: string;
  leadId?: string;
  viewArchived?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateProjectInput {
  contactId: number;
  type: string;
  subtype?: string | null;
  name: string;
  leadId?: string | null;
  desiredDeadline?: string | null;
}

export interface UpdateProjectInput {
  name?: string;
  status?: string;
  type?: string;
  subtype?: string | null;
  briefData?: string | null;
  contentPlan?: string | null;
  templateName?: string | null;
  previewUrl?: string | null;
  storagePath?: string | null;
  desiredDeadline?: string | null;
  language?: string;
}

function buildQuery(filters?: ProjectFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.contactId !== undefined) params.set('contactId', String(filters.contactId));
  if (filters.type) params.set('type', filters.type);
  if (filters.subtype) params.set('subtype', filters.subtype);
  if (filters.leadId) params.set('leadId', filters.leadId);
  if (filters.viewArchived) params.set('viewArchived', 'true');
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Invalida las dos claves de un proyecto, si la respuesta trajo proyecto.
 *
 * No siempre lo trae: una iteración que no cambió nada no crea versión, así que
 * no hay proyecto nuevo que anunciar. Desreferenciarlo igual tiraba
 * "Cannot read properties of undefined (reading 'id')" adentro de onSuccess, y
 * eso además hace que mutateAsync rechace — el error se ve dos veces y el
 * mensaje real de la operación no se ve nunca.
 */
function invalidarProyecto(qc: QueryClient, project: ProjectRow | null | undefined): void {
  if (!project) return;
  void qc.invalidateQueries({ queryKey: ['project', project.id] });
  void qc.invalidateQueries({ queryKey: ['project', project.slug] });
}

export function useProjects(filters?: ProjectFilters) {
  return useQuery<ProjectsResult>({
    queryKey: ['projects', filters ?? {}],
    queryFn: () => api.get<ProjectsResult>(`/api/projects${buildQuery(filters)}`),
    staleTime: 30_000,
  });
}

export function useProjectByLeadId(leadId: string | null | undefined) {
  return useQuery<ProjectsResult>({
    queryKey: ['projects', { leadId }],
    queryFn: () => api.get<ProjectsResult>(`/api/projects?leadId=${leadId!}&limit=1`),
    enabled: !!leadId,
    staleTime: 30_000,
  });
}

export function useContactProjects(contactId: number | undefined) {
  return useQuery<ProjectsResult>({
    queryKey: ['projects', { contactId }],
    queryFn: () => api.get<ProjectsResult>(`/api/projects?contactId=${contactId!}`),
    enabled: contactId !== undefined,
    staleTime: 30_000,
  });
}

export function useProject(idOrSlug: string | number | undefined) {
  return useQuery<ProjectRow>({
    queryKey: ['project', idOrSlug],
    queryFn: () => api.get<ProjectRow>(`/api/projects/${idOrSlug!}`),
    enabled: idOrSlug !== undefined,
    staleTime: 30_000,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api.post<ProjectRow>('/api/projects', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number } & UpdateProjectInput) =>
      api.patch<ProjectRow>(`/api/projects/${id}`, input),
    onSuccess: (data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ deleted: boolean }>(`/api/projects/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useSaveBrief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, briefData, transitionToBriefed }: {
      id: number;
      briefData: object;
      transitionToBriefed?: boolean;
    }) => api.post<ProjectRow>(`/api/projects/${id}/brief`, { briefData, transitionToBriefed }),
    onSuccess: data => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export function useSkipBrief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<ProjectRow>(`/api/projects/${id}/skip-brief`, {}),
    onSuccess: data => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export function useDeleteAsset() {
  return useMutation({
    mutationFn: ({ projectId, path }: { projectId: number; path: string }) =>
      api.delete<{ deleted: boolean }>(`/api/projects/${projectId}/assets`, { path }),
  });
}

export function useBulkArchiveProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      api.post<{ updated: number }>('/api/projects/bulk/archive', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useBulkDeleteProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      api.post<{ deleted: number }>('/api/projects/bulk/delete', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useCreateProjectFromLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      api.post<ProjectRow & { alreadyExists?: boolean }>(`/api/leads/${leadId}/create-project`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

interface ContentPlanResponse {
  contentPlan: ContentPlan;
  project: ProjectRow;
}

export function useGenerateContentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ContentPlanResponse>(`/api/projects/${id}/generate-content-plan`, {}),
    onSuccess: (data) => {
      // Update cache immediately so the Claude panel sees the new status
      // without waiting for the background refetch.
      if (data.project) {
        qc.setQueryData(['project', data.project.id], data.project);
        qc.setQueryData(['project', data.project.slug], data.project);
      }
      void qc.invalidateQueries({ queryKey: ['projects'] });
      invalidarProyecto(qc, data.project);
    },
  });
}

export function useRegeneratePlanPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, page }: { id: number; page: string }) =>
      api.post<ContentPlanResponse>(`/api/projects/${id}/content-plan/regenerate-page`, { page }),
    onSuccess: (data) => {
      if (data.project) {
        qc.setQueryData(['project', data.project.id], data.project);
        qc.setQueryData(['project', data.project.slug], data.project);
      }
      void qc.invalidateQueries({ queryKey: ['projects'] });
      invalidarProyecto(qc, data.project);
    },
  });
}

export function useUpdateContentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contentPlan }: { id: number; contentPlan: ContentPlan }) =>
      api.patch<ContentPlanResponse>(`/api/projects/${id}/content-plan`, { content_plan: contentPlan }),
    onSuccess: (data) => {
      invalidarProyecto(qc, data.project);
    },
  });
}

export function useRefineContentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, instruction }: { id: number; instruction: string }) =>
      api.post<ContentPlanResponse>(`/api/projects/${id}/content-plan/refine`, { instruction }),
    onSuccess: (data) => {
      invalidarProyecto(qc, data.project);
    },
  });
}

export function useStartDesign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ProjectRow>(`/api/projects/${id}/start-design`, {}),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export interface GenerateReportResponse {
  reportId: number;
  reportSlug: string;
  contentPlan: ContentPlan;
}

export function useGenerateContentReport() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<GenerateReportResponse>(`/api/projects/${id}/content-plan/generate-report`, {}),
  });
}

export interface TemplateEntry {
  slug: string;
  name: string;
  directory: string;
  entry: string;
  showcase: string;
  default_accent: string;
  default_font: string;
  overridable_tokens: string[];
  personality: string;
}

export interface TemplateManifest {
  version: number;
  templates: TemplateEntry[];
}

export function useTemplateManifest() {
  return useQuery<TemplateManifest>({
    queryKey: ['template-manifest'],
    queryFn: () => api.get<TemplateManifest>('/templates/manifest.json'),
    staleTime: Infinity,
  });
}

export function useAssignTemplate(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateSlug: string) =>
      api.patch<ProjectRow>(`/api/projects/${projectId}/template`, { template_slug: templateSlug }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export interface PendingImage {
  filename: string;
  width: number;
  height: number;
  role: string;
}

export interface PendingImagesFile {
  generatedAt: string | null;
  templateSlug: string | null;
  images: Array<PendingImage & { uploaded: boolean; uploadedAt: string | null }>;
}

export interface GeneratePageResponse {
  pendingImages: PendingImage[];
  project: ProjectRow;
  /** Qué página se generó y en qué archivo quedó. */
  page?: string;
  file?: string;
  /** Las del plan que todavía no tienen archivo. Vacío = el sitio está entero. */
  pendingPages?: string[];
}

export function useGeneratePage(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (page?: string) =>
      api.post<GeneratePageResponse>(`/api/projects/${projectId}/generate-page`, page ? { page } : {}),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      invalidarProyecto(qc, data.project);
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
      void qc.invalidateQueries({ queryKey: ['project-preview-pages', projectId] });
    },
  });
}

/**
 * Rehace UNA página desde el plan, creando versión nueva.
 *
 * Distinto de iterar: iterar parte de lo que hay y pide un cambio; esto la
 * escribe de nuevo, que es lo que sirve cuando una página salió mal y explicar
 * qué arreglar cuesta más que rehacerla.
 */
export function useRegeneratePage(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (page: string) =>
      api.post<GeneratePageResponse & { version: number }>(`/api/projects/${projectId}/regenerate-page`, { page }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      invalidarProyecto(qc, data.project);
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
      void qc.invalidateQueries({ queryKey: ['project-preview-pages', projectId] });
      void qc.invalidateQueries({ queryKey: ['project-versions', projectId] });
    },
  });
}

export function usePendingImages(projectId: number | undefined, version?: number) {
  const vParam = version !== undefined ? `?v=${version}` : '';
  return useQuery<PendingImagesFile>({
    queryKey: ['pending-images', projectId, version],
    queryFn: () => api.get<PendingImagesFile>(`/api/projects/${projectId!}/pending-images${vParam}`),
    enabled: projectId !== undefined,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useRevisionPendingImages(projectId: number | undefined, revisionNumber: number | undefined) {
  return useQuery<PendingImagesFile>({
    queryKey: ['revision-pending-images', projectId, revisionNumber],
    queryFn: () => api.get<PendingImagesFile>(
      `/api/projects/${projectId!}/revisions/${revisionNumber!}/pending-images`,
    ),
    enabled: projectId !== undefined && revisionNumber !== undefined,
    staleTime: Infinity, // revision snapshot is frozen in DB at approval — no invalidation path needed
  });
}

export interface UploadImageResponse {
  uploadedAt: string;
  remainingPending: number;
}

export function useUploadProjectImage(projectId: number, version?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, filename }: { file: File; filename: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', filename);
      const vParam = version !== undefined ? `?v=${version}` : '';
      return api.upload<UploadImageResponse>(`/api/projects/${projectId}/upload-image${vParam}`, formData);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
    },
  });
}

export interface VersionInfo {
  version: number;
  generatedAt: string | null;
  /** 'manual' = la produjo el editor visual, sin modelo. */
  source: 'ai' | 'manual';
}

export interface VersionsResponse {
  currentVersion: number;
  versions: VersionInfo[];
}

export interface IteratePageResponse {
  version: number;
  pendingImages: PendingImage[];
  notes: string;
  project: ProjectRow;
}

export interface PreviewPage {
  /** Ruta relativa dentro de la versión: 'index.html', 'en/index.html'. */
  file: string;
  name: string;
}

/**
 * Las páginas que tiene una versión del preview.
 *
 * El preview es multipágina desde siempre —Scrapmen tiene index, firma,
 * email-preview y la home en inglés— pero el visor abría index.html y nada
 * más. Esto es lo que alimenta el selector de la banda.
 */
export function useProjectPreviewPages(projectId: number | undefined, version?: number) {
  return useQuery<{ pages: PreviewPage[]; version: number }>({
    queryKey: ['project-preview-pages', projectId, version],
    queryFn: () => api.get<{ pages: PreviewPage[]; version: number }>(
      `/api/projects/${projectId!}/preview/pages${version ? `?v=${version}` : ''}`,
    ),
    enabled: projectId !== undefined,
    staleTime: 0,
  });
}

export function useProjectVersions(projectId: number | undefined) {
  return useQuery<VersionsResponse>({
    queryKey: ['project-versions', projectId],
    queryFn: () => api.get<VersionsResponse>(`/api/projects/${projectId!}/versions`),
    enabled: projectId !== undefined,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useIteratePage(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ instruction, file }: { instruction: string; file?: string }) =>
      api.post<IteratePageResponse>(`/api/projects/${projectId}/iterate-page`, { instruction, ...(file ? { file } : {}) }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      invalidarProyecto(qc, data.project);
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
      void qc.invalidateQueries({ queryKey: ['project-versions', projectId] });
    },
  });
}

export type ManualEditOp =
  | {
      /** setOwnText cambia solo el texto propio, dejando los elementos hijos intactos. */
      op: 'setText' | 'setOwnText' | 'setRichText';
      targetId: string;
      value: string;
    }
  | { op: 'setClasses'; targetId: string; add: string[]; remove: string[] }
  /** Único atributo que el editor puede escribir, y solo sobre un <a>. */
  | { op: 'setHref'; targetId: string; value: string }
  /** Párrafo ↔ encabezado. */
  | { op: 'setTag'; targetId: string; value: string }
  /**
   * Ajuste de estilo, opcionalmente solo en una vista. `value: null` lo borra.
   * No escribe style="": el servidor lo guarda en una hoja propia del proyecto.
   */
  | {
      op: 'setStyle';
      targetId: string;
      viewport: OverrideViewport;
      prop: OverrideProp;
      value: string | null;
    }
  /** Los ids de la copia los manda el cliente: ver DuplicateNodeOp en el servidor. */
  | { op: 'duplicateNode'; targetId: string; idMap: Record<string, string> }
  | { op: 'removeNode'; targetId: string }
  | { op: 'moveNode'; targetId: string; direction: 'up' | 'down' };

export type UtilityGroupId =
  | 'align' | 'textSize' | 'textColor' | 'padding' | 'margin' | 'gap' | 'size';

export interface UtilityGroup {
  id: UtilityGroupId;
  /** Vacío = este template no puede expresar esa propiedad. */
  options: string[];
}

export interface ManualEditEntry {
  targetId: string;
  type: 'text' | 'class' | 'href' | 'tag' | 'structure';
  added?: string[];
  removed?: string[];
  at: string;
  version: number;
}

export interface EditorContext {
  templateSlug: string;
  groups: UtilityGroup[];
  /**
   * Encabezados que el template estila por etiqueta. Vacío = convertir un
   * párrafo en encabezado no se vería, y el panel lo dice.
   */
  headingTags: string[];
  manualEdits: ManualEditEntry[];
  /** Ajustes por vista ya guardados, para que el panel muestre el valor puesto. */
  overrides: OverrideRule[];
}

/** Qué puede expresar el template del proyecto, y qué se editó a mano. */
export function useEditorContext(projectId: number | undefined, enabled: boolean) {
  return useQuery<EditorContext>({
    queryKey: ['editor-context', projectId],
    queryFn: () => api.get<EditorContext>(`/api/projects/${projectId!}/editor-context`),
    enabled: enabled && projectId !== undefined,
    staleTime: 0,
  });
}

export interface ApplyEditsResponse {
  version: number;
  applied: number;
  failed: number;
  failuresDetail: string | null;
  pendingImages: PendingImage[];
  project: ProjectRow;
}

/**
 * Guarda ediciones manuales del editor visual. No pasa por Claude: el servidor
 * aplica las operaciones y crea la versión siguiente.
 */
export function useApplyEdits(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    // `hallazgo` lo manda solo el panel de salud: dice qué hallazgo resuelve
    // esta edición, y con eso llega al reporte del cliente.
    mutationFn: ({ ops, file, hallazgo }: { ops: ManualEditOp[]; file?: string; hallazgo?: Record<string, unknown> }) =>
      api.post<ApplyEditsResponse>(`/api/projects/${projectId}/apply-edits`, {
        ops, ...(file ? { file } : {}), ...(hallazgo ? { hallazgo } : {}),
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      invalidarProyecto(qc, data.project);
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
      void qc.invalidateQueries({ queryKey: ['project-versions', projectId] });
      // El panel de salud vuelve a analizar la página: un hallazgo arreglado
      // tiene que desaparecer de la lista, no quedar con un "aplicado" al lado.
      void qc.invalidateQueries({ queryKey: ['hallazgos', projectId] });
    },
  });
}

// ── Comentarios sobre la página ────────────────────────────────────────────────
//
// La otra herramienta del editor: en vez de tocar la página, se deja una
// instrucción anclada y la resuelve el modelo. Se acumulan y se aplican juntas.

export interface PageComment {
  id: string;
  kind: 'element' | 'region';
  targetIds: string[];
  tag?: string;
  snippet?: string;
  rect?: { top: number; left: number; width: number; height: number };
  imageFile?: string;
  text: string;
  status: 'pending' | 'applied' | 'failed';
  /** Qué dijo el modelo de este comentario al aplicarlo. */
  note?: string;
  createdAt: string;
  createdInVersion: number;
  appliedInVersion?: number;
}

export interface NewComment {
  kind: 'element' | 'region';
  targetIds: string[];
  text: string;
  rect?: { top: number; left: number; width: number; height: number };
  /** Ancho con el que se estaba mirando la página, para recortar la captura. */
  viewportWidth?: number;
  /** Página comentada, relativa a la carpeta de la versión. */
  file?: string;
}

export interface ApplyCommentsResponse {
  version: number;
  applied: number;
  pending: number;
  comments: PageComment[];
  notes: string;
  /** Lo que el modelo escribió fuera del sistema de diseño. Medido, no declarado. */
  systemEscapes: string;
  /** Qué cambió por fuera del elemento marcado. Medido sobre el archivo. */
  outsideAnchor?: string;
  /** true si la página quedó igual: no se creó versión nueva. */
  sinCambios?: boolean;
  project?: ProjectRow;
}

export function useComments(projectId: number | undefined, enabled: boolean) {
  return useQuery<{ comments: PageComment[] }>({
    queryKey: ['page-comments', projectId],
    queryFn: () => api.get<{ comments: PageComment[] }>(`/api/projects/${projectId!}/comments`),
    enabled: enabled && projectId !== undefined,
    staleTime: 0,
  });
}

export function useCreateComment(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (comment: NewComment) =>
      api.post<{ comment: PageComment; captureError?: string }>(
        `/api/projects/${projectId}/comments`, comment,
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['page-comments', projectId] }); },
  });
}

export function useDeleteComment(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      api.delete<{ ok: boolean }>(`/api/projects/${projectId}/comments/${commentId}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['page-comments', projectId] }); },
  });
}

// ── SEO del borrador ────────────────────────────────────────────────────────
// Análisis estático sobre el HTML de la versión en revisión. No pasa por el
// modelo: es código determinista, y por eso no puede inventar un texto
// alternativo que nadie escribió.

export interface SeoFinding {
  check: string;
  severity: 'alta' | 'media' | 'baja';
  title: string;
  detail: string;
  targetId?: string;
  /** true = hace falta que una persona aporte contenido; no se manda al generador. */
  necesitaPersona: boolean;
}

export interface SeoAuditResponse {
  file: string;
  version: number;
  findings: SeoFinding[];
  resumen: { imagenes: number; encabezados: number; enlaces: number };
  /** Cuántos hallazgos puede arreglar el generador solo. */
  aplicables: number;
}

export function useSeoAudit(projectId: number | undefined, enabled: boolean) {
  return useQuery<SeoAuditResponse>({
    queryKey: ['seo-audit', projectId],
    queryFn: () => api.get<SeoAuditResponse>(`/api/projects/${projectId!}/seo-audit`),
    enabled: enabled && projectId !== undefined,
    staleTime: 0,
  });
}

export interface SeoLiveResponse {
  url: string;
  strategy: 'mobile' | 'desktop';
  puntajes: { performance: number | null; accessibility: number | null; bestPractices: number | null; seo: number | null };
  fallidas: Array<{ id: string; title: string; category: string }>;
  medidoEn: string;
}

/** PageSpeed sobre el sitio YA PUBLICADO. Distinto del análisis del borrador. */
export function useSeoLive(projectId: number | undefined, enabled: boolean) {
  return useQuery<SeoLiveResponse>({
    queryKey: ['seo-live', projectId],
    queryFn: () => api.get<SeoLiveResponse>(`/api/projects/${projectId!}/seo-live`),
    enabled: enabled && projectId !== undefined,
    // Cuesta cuota diaria de PageSpeed: no se repite sola al volver a la pestaña.
    staleTime: 15 * 60_000,
    retry: false,
  });
}

export function useSeoToComments(projectId: number) {
  const qc = useQueryClient();
  return useMutation<{ creados: number; necesitanPersona: string[] }>({
    mutationFn: () => api.post(`/api/projects/${projectId}/seo-audit/to-comments`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['page-comments', projectId] });
      void qc.invalidateQueries({ queryKey: ['seo-audit', projectId] });
    },
  });
}

export function useApplyComments(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file?: string) =>
      api.post<ApplyCommentsResponse>(`/api/projects/${projectId}/comments/apply`, file ? { file } : {}),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['page-comments', projectId] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      invalidarProyecto(qc, data.project);
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
      void qc.invalidateQueries({ queryKey: ['project-versions', projectId] });
    },
  });
}

// ── Approval cycle ─────────────────────────────────────────────────────────────

export interface RevisionPendingImagesSummary {
  total: number;
  uploaded: number;
  pending: number;
}

export interface RevisionEntry {
  revisionNumber: number;
  approvedAt: string;
  approvalNote: string | null;
  workingVersionsCount: number;
  pendingImagesSummary: RevisionPendingImagesSummary;
  archiveAvailable: boolean;
}

export interface ApproveProjectResponse {
  revisionNumber: number;
  approvedAt: string;
  archivePath: string | null;
}

export interface NewRevisionResponse {
  newVersion: number;
  basedOnRevision: number;
  project: ProjectRow;
}

export function useApproveProject(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ note }: { note?: string }) =>
      api.post<ApproveProjectResponse>(`/api/projects/${projectId}/approve`, { note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['project-revisions', projectId] });
    },
  });
}

export function useNewRevision(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<NewRevisionResponse>(`/api/projects/${projectId}/new-revision`, {}),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      invalidarProyecto(qc, data.project);
    },
  });
}

export function useProjectRevisions(projectId: number | undefined) {
  return useQuery<RevisionEntry[]>({
    queryKey: ['project-revisions', projectId],
    queryFn: () => api.get<RevisionEntry[]>(`/api/projects/${projectId!}/revisions`),
    enabled: projectId !== undefined,
    staleTime: 30_000,
  });
}

// ── ADA/SEO Audits ────────────────────────────────────────────────────────────

export interface AuditSummary {
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
}

export interface AuditViolation {
  id: string;
  impact: string;
  description: string;
  helpUrl: string;
  nodeCount: number;
  /** Dónde falla cada uno. Solo viene en el detalle, no en el listado. */
  nodes?: Array<{ selector: string; html: string }>;
  /** Si el agente de sitios puede tomarlo. Lo decide el servidor. */
  arreglable?: boolean;
  /** Por qué no, cuando no. */
  motivoNoArreglable?: string | null;
}

export interface AuditListItem {
  id: number;
  targetType: 'working_version' | 'revision';
  targetVersion: number;
  auditedAt: string;
  summary: AuditSummary;
  score: number | null;
}

export interface AuditDetail extends AuditListItem {
  violations: AuditViolation[];
  autoFixesApplied: Array<{ name: string; description: string }>;
}

export interface RunAuditResult {
  reportId: number;
  summary: AuditSummary;
  violations: AuditViolation[];
  autoFixesApplied: Array<{ name: string; description: string }>;
  score: number;
}

export function useProjectAudits(projectId: number | undefined) {
  return useQuery<AuditListItem[]>({
    queryKey: ['project-audits', projectId],
    queryFn: () => api.get<AuditListItem[]>(`/api/projects/${projectId!}/audits`),
    enabled: projectId !== undefined,
    staleTime: 60_000,
  });
}

/** Detalle de una auditoría: los hallazgos, con selectores y HTML de cada nodo. */
export function useAuditDetail(projectId: number, auditId: number | undefined) {
  return useQuery<AuditDetail>({
    queryKey: ['project-audit', projectId, auditId],
    queryFn: () => api.get<AuditDetail>(`/api/projects/${projectId}/audits/${auditId!}`),
    enabled: auditId !== undefined,
    staleTime: 60_000,
  });
}

/**
 * Manda UN hallazgo al agente de sitios. El agente trabaja sobre la copia local
 * y el resultado se mira en Revisión — esto no publica nada.
 */
export function useFixViolation(projectId: number, auditId: number | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (violationId: string) =>
      api.post<{ taskId: string; reglaId: string; nodeCount: number }>(
        `/api/projects/${projectId}/audits/${auditId!}/fix`, { violationId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useRunAudit(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    // `published` audita el sitio EN VIVO; sin fuente, la versión local. Son dos
    // preguntas distintas: cómo está lo que el cliente ve, y cómo va a quedar lo
    // que todavía no se publicó.
    mutationFn: (source?: 'local' | 'published') =>
      api.post<RunAuditResult>(`/api/projects/${projectId}/audit`, source ? { source } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-audits', projectId] });
    },
  });
}

// ── AI Image Generation ────────────────────────────────────────────────────────

export interface GenerateImageResult {
  ok: boolean;
  remainingPending: number;
  /** Exact cost in USD calculated from OpenAI usage. null when usage was not returned. */
  costUsd: number | null;
}

export function useGenerateImage(projectId: number, version?: number) {
  const qc = useQueryClient();
  const vParam = version !== undefined ? `?v=${version}` : '';
  return useMutation({
    mutationFn: ({ filename, prompt }: { filename: string; prompt?: string }) =>
      api.post<GenerateImageResult>(
        `/api/projects/${projectId}/generate-image${vParam}`,
        { filename, prompt },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
    },
  });
}
